import propTypes from 'prop-types'
import i18n from 'live/services/i18n'
import {HighchartBase} from '@digital-widgets/widget-helper'
import XRange from 'highcharts/modules/xrange'
import patternFill from 'highcharts/modules/pattern-fill'
import Highcharts from 'highcharts'
import debounce from 'lodash.debounce'
import moment from 'moment'
import Logger from 'live/services/logger'
import pointService from 'live/services/charts/point'
import ZoomManager, { ZoomManagerZoomInNotifier$, ZoomManagerZoomOutNotifier$ } from 'live/services/zoom-manager'

import React, {useCallback, useEffect, useState} from 'react'
import HelpTip from 'live/components/ui/help-tip'
import widgetsService from 'live/services/widgets'

XRange(Highcharts)
patternFill(Highcharts)

/** xrange-widget
 *  Example usage for snapshot query:
 *  see README
 */

const WIDGET_TYPE = 'xrange2'
const HIGHCHARTS_WIDGET_TYPE = 'xrange'
const LOCAL_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const DEFAULT_CHART_CONFIG = {
    hideTooltip: false
}

const DEFAULT_COLOR = '#0B8FD9'
const LEGACY_DURATION_LIMIT_COLOR = '#EE6B1D'
const LEGEND_PROXY_SERIES_PREFIX = 'xrange-color-legend-'

/**
 * Widget class for rendering and managing an XRange chart widget using Highcharts.
 *
 * This widget is designed to visualize time intervals (x-range events) across multiple categories,
 * supporting features such as dynamic category management, open/close interval tracking, custom tooltips,
 * and integration with dashboard timespan controls.
 *
 * Key Features:
 * - Dynamically updates chart data and categories based on incoming events.
 * - Handles both snapshot and real-time event modes.
 * - Supports clickable Y-axis labels with URLs.
 * - Displays a "No data" message when no events or categories are present.
 * - Integrates with dashboard timespan selection and linking.
 * - Custom tooltip formatting for interval duration.
 * - Manages open intervals and extrapolates their end times if needed.
 *
 * @class
 * @extends HighchartBase
 *
 * @property {Object} policies - Static configuration policies for the widget.
 * @property {Object} noDataMessagePosition - Static position for the "No data" message.
 * @property {string} noDataMessageText - Static text for the "No data" message.
 *
 * @param {Object} options - Widget initialization options.
 * @param {Object} options.dashboardContext - Dashboard context, including actions and span.
 * @param {Function} options.onSelection - Callback for selection events.
 *
 * @method onReady - Called when the widget is ready; triggers chart update.
 * @method onEvents - Handles incoming event data, updating chart data accordingly.
 * @method createContainer - Creates or retrieves the chart container DOM element.
 * @method render - Initializes and renders the Highcharts XRange chart.
 * @method onEndHistory - Called at the end of history navigation; updates open intervals.
 * @method onSpan - Handles timespan changes, updating chart extremes.
 * @method onStart - Handles the start of a new data stream, resetting state and categories.
 * @method goToURL - Opens a URL in a new tab when a data point is clicked.
 * @method changeTimespan - Handles chart selection events to update dashboard timespan.
 * @method updateCategories - Updates the chart's Y-axis categories.
 * @method updateOpenIntervals - Updates open intervals for real-time extrapolation.
 *
 * @example
 * const widget = new Widget({ dashboardContext: {...}, onSelection: fn });
 * widget.onStart('start', startEvent);
 * widget.onEvents('events', eventsArray);
 */
class Widget extends HighchartBase {
    static policies = {
        // Notice on disableLatestOptimization:
        // This @meta prevents the queries from trimming the preload windows. The preload is important in @xrange functions, because they allow to build an initial state before the start of user span
        // Trick: the metadata in the reducer does not actually work (it is indeed known to the Pipes engine, but should be inside the main query).
        //        adding it to the Reducer works in an indirect way: any reducer different from '@latest' will not trigger the Preload optimization.
        //        I.e., we could add any String to the reducer expression, as long as it does not alter the @latest result. This one acts as a "purpose documentation" for changing the normal '@latest' reducer
        defaultReducer: '@latest => @meta true as disableLatestOptimization',
        currentCrosshairValueCapacity: true,
        layers: 1
    }

    static noDataMessagePosition = { x: 0, y: 0, align: 'center', verticalAlign: 'middle' }
    static noDataMessageText = 'No data found in the selected period'

    constructor(options) {
        super(options)

        this.logger = new Logger('XRange2')
        this.handleMouse = false
        this.updateHighcharts = debounce(() => {
            if (this.span) {
                const span = this.span
                if (this.chart.xAxis)
                    this.chart.xAxis[0].setExtremes(span.begin, span.end)
                // this.chart.update({ xAxis: { min: span.begin, max: span.end } })
            }

            let chart = this.get('xRangeEvents')

            const data = []

            /* xrange needs a data point referencing so labels are shown in the y axis, we want to always display categories but without altering mutating this.data */
            const seen = new Set()

            this.data.forEach(point => {
                seen.add(point.y)
                data.push(point)
            })

            // ensure sorted by x
            data.sort((a, b) => (a.x > b.x ? 1 : -1))

            this.logger.debug('setData', data)

            chart.setData(data, true, false, true) // redraw, animation, updatePoints

            chart = chart.chart

            // After the chart data is refreshed, re-sync the legend proxy series so that
            // event-derived colors (registered via registerEventColorLegendEntry) are reflected.
            this.syncLegendProxySeries()

            /* ensure message placeholder is shown when no categories were defined */
            if (!this.data.length && !this.categories.length) {
                const msg = $(this.getEl()).closest('.widget-container').find('.msg-box span').first().text()

                // only show here if not already shown by widget container
                if (msg && msg.indexOf('No data') > -1) {
                    this.noDataLabel = this.noDataLabel && this.noDataLabel.destroy()

                    return
                }

                if (!this.noDataLabel && chart.renderer) {
                    this.noDataLabel = chart.renderer.label(
                        Widget.noDataMessageText,
                        0,
                        0,
                        null,
                        null,
                        null,
                        true,
                        null,
                        'no-data'
                    )
                    // TODO: style this.noDataLabel.attr(b.attr).css(b.style)
                    this.noDataLabel.add()
                    this.noDataLabel.align(
                        Object.assign({}, this.noDataLabel.getBBox(), Widget.noDataMessagePosition),
                        false,
                        'plotBox'
                    )
                }
            } else if (this.noDataLabel) {
                this.noDataLabel = this.noDataLabel.destroy()
            }
        }, 150)

        if (options.dashboardContext.actions) {
            this.setTimespan = options.dashboardContext.actions.setTimespan
            this.defaultTimespan = options.dashboardContext.dashboard.span
            this.onSelection = options.onSelection
        }

        this.categories = []
        this.colorRules = {}
        this.durationLimitRules = []
        this.unbatchedMode = false

        this.data = []

        this.eventColorLegendEntries = {}
        this.ambiguousEventColorLegendEntries = new Set()

        // Dashboard-synced ctrl+wheel zoom / shift+wheel pan state, mirroring live-core's commonZoom mixin
        // (webapp/js/live-core/widgets/highcharts-prototype.ts) used by the temporal widget
        this.hasAxisExtremesSet = false
        this.chartInRealTime = false
        this.toCommitAxisExtremes = false
        this.toSetPreviousZoom = null

        this.initializeZoom()
    }

    // ===========================
    // Core widget lifecycle
    // ===========================

    onReady() {
        this.updateHighcharts()
    }

    onEvents(type, events) {
        if (!this.unbatchedMode) {
            // By default, each batch contains the full set of ranges to be rendered (i.e., clear points on every new batch)
            this.data = []
        }

        // Reset per-event color tracking at the start of each event batch so that
        // stale entries from a previous render do not pollute the legend.
        this.resetEventColorLegendEntries()

        events.forEach(event => {
            let y = typeof event.y === 'string'
                ? this.categories.indexOf(event.y)
                : typeof event.y === 'number' ? event.y : null

            if (y == null) {
                this.logger.warn('Event has missing or invalid y value, ignoring', event)
                return
            }

            if (y < 0 && typeof event.y === 'string') {
                // Allow to automatically infer categories from events
                y = this.categories.length
                this.categories.push(event.y);
                this.updateCategories(this.categories)
            }

            let x = event.xStart
            let x2 = event.xEnd

            // TODO: xrange output was refactored to replace the status "active" with "visible", to make its purpose clearer and avoid clashing with other Active statuses that may happen in the domain. Remove `event.active` fallback once all queries have been migrated to the new `visible` field name
            if (event.visible ?? event.active) {
                if (x2 == null && !event.closed) {
                    // Extrapolate end time for open intervals until the end of the current span
                    x2 = Math.min(new Date().getTime(), this.span ? this.span.end : Infinity)
                }

                const itemColor = event.color ? event.color // The color informed in the event data is the priority
                    : this.colorRules[event.value] ? this.colorRules[event.value]  // Then try to find based on the event value + @meta colors rules
                    : Array.isArray(event.value) && event.value.length > 0 && this.colorRules[event.value[0]] ? this.colorRules[event.value[0]] // When the value is an array, this is common when we have a merged range with multiple values
                        : DEFAULT_COLOR

                this.registerEventColorLegendEntry(event)

                let background = null
                let customTooltipText = null

                /*
                3 types of interval coloring:
                   - Merged intervals: display as thin vertical lines with pattern fill, indicating fast status changes
                   - Intervals that exceed duration limits: Use gradient to display a strip at the bottom with the color of the severity
                   - Regular intervals: use solid color based on event value or default
                 */
                if (event.mergeInfo?.merged) {
                    const mergedValueColors = Array.isArray(event.value) && event.value.length > 0
                        ? event.value.map(val => this.colorRules[val] || itemColor)
                        : [itemColor]

                    const hasMultipleStatuses = mergedValueColors.length > 1 && mergedValueColors[0] !== mergedValueColors[1]

                    if (!hasMultipleStatuses) {
                        // The Pattern background with vertical lines looks great to represent the intermittent status,
                        // but it only supports a single color - unfortunately the background color does not work with xrange charts (in our Highcharts version at least)
                        // Use when we have just one status in the merged interval
                        background = {
                            pattern: {
                                path: 'M 0 0 L 0 10 M 1 0 L 1 10',
                                color: mergedValueColors[0],
                                width: 3,
                                height: 10,
                                opacity: 0.9,
                            }
                        }
                    } else {
                        // If the status oscillates between multiple values, we simulate a striped pattern using a horizontal
                        // linear gradient with small space between color changes

                        // The mergedValueColors can contain from 2 to n colors. We create a gradient with stripes
                        // 0.05 far from each other, until we cover all the interval from 0 to 1 (repeat the colors as necessary)
                        const stops = []
                        let offset = 0
                        let colorIndex = 0
                        while (offset <= 1) {
                            stops.push([offset, mergedValueColors[colorIndex]])
                            offset += 0.03
                            colorIndex = (colorIndex + 1) % mergedValueColors.length
                        }

                        background = {
                            linearGradient: {x1: 0, x2: 1, y1: 0, y2: 0},
                            stops: stops
                        }
                    }

                    customTooltipText =
                        `<strong>High Frequency interval</strong><br />Status change is too fast for proper visualization<br />Apply zoom if you need to see individual ranges`

                    if (event.mergeInfo.summary) {
                        // Object with summary of ranges merged in the interval: [ { "value": "value1", "count": 63, "duration": 181382 }, {"value": "value2", ...} ]

                        const mergeSummaryText = event.mergeInfo.summary.map(summaryItem => {
                                const label = summaryItem.value === 1 || summaryItem.value === 1.0 || summaryItem.value === true || summaryItem.value === 'true' ? 'Active'
                                    : summaryItem.value

                                if (summaryItem.value && summaryItem.count && summaryItem.duration) {
                                    return `<br /><span style="color: ${this.colorRules[summaryItem.value] || itemColor};">${label}:</span> ${summaryItem.count ?? '-'} times, active duration ${Widget.formatDuration(summaryItem.duration)}`
                                } else if (label) {
                                    return `<br /><span style="color: ${this.colorRules[summaryItem.value] || itemColor};">${label}</span>`
                                } else {
                                    return ''
                                }
                            }
                        )
                        customTooltipText = customTooltipText + mergeSummaryText.join('')
                    }

                } else {
                    const duration = x2 - x
                    let limitRule = null

                    // Preferred path: use modern duration limit rules configured via @meta durationLimits.
                    if (this.durationLimitRules && this.durationLimitRules.length > 0) {
                        limitRule = this.durationLimitRules.find(rule => duration >= rule.limit)
                    }

                    // Compatibility mode: legacy queries can send a per-event `limit_ms` threshold.
                    // Accept both finite numbers and numeric strings in milliseconds.
                    // If valid and no modern rule matched, apply a single orange duration marker.
                    if (!limitRule && event.limit_ms != null) {
                        const rawLegacyLimit = event.limit_ms
                        let legacyLimit = null

                        if (typeof rawLegacyLimit === 'number' && Number.isFinite(rawLegacyLimit)) {
                            legacyLimit = rawLegacyLimit
                        } else if (typeof rawLegacyLimit === 'string' && rawLegacyLimit.trim() !== '') {
                            const parsedLegacyLimit = Number(rawLegacyLimit.trim())
                            if (Number.isFinite(parsedLegacyLimit)) {
                                legacyLimit = parsedLegacyLimit
                            }
                        }

                        if (legacyLimit != null && duration >= legacyLimit) {
                            limitRule = {
                                limit: legacyLimit,
                                color: LEGACY_DURATION_LIMIT_COLOR
                            }
                        }
                    }

                    if (limitRule) {
                        // dataPoint.borderColor = limitRule.color
                        // dataPoint.borderWidth = 3

                        // background = {
                        //     // pattern: {
                        //     //     path: 'M 0 0 L 5 0',
                        //     //     width: 5,
                        //     //     height: 100,
                        //     //     color: limitRule.color,
                        //     //     opacity: 0.8,
                        //     //     backgroundColor: '#99ee9900'
                        //     // }
                        //     pattern: {
                        //         // 'M 0 0 H 10 V 10 H 0 Z' desenha um quadrado de 10x10
                        //         // 'M 5 0 L 5 10' desenha a sua linha vertical no meio (x=5)
                        //         path: 'M -1 -1 H 10 V 10 H 0 Z M 5 0 L 5 10',
                        //         width: 10,
                        //         height: 10,
                        //         color: '#f00', // Cor da linha
                        //         // fill: '#00f' // AQUI você define a cor de fundo
                        //     }
                        // }

                        background = {
                            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                            stops: [
                                [0, itemColor],
                                [0.75, itemColor],
                                [1, limitRule.color]
                            ]
                        }

                        customTooltipText = `<span style="color: ${limitRule.color};">Duration exceeds ${moment.utc(limitRule.limit).format('HH:mm:ss')}</span>`
                    }
                }

                // Fallback to solid colors for regular intervals
                if (!background) {
                    background = itemColor
                }

                const dataPoint = {
                    y,
                    x,
                    x2,
                    value: event.value,
                    color: background,
                    bulletColor: itemColor,
                    url: event.url,
                    className: event.extrapolated === true || event.open === true ? 'open-interval' : null,
                    customTooltipText: customTooltipText,
                }

                this.data.push(dataPoint)
            }
        })

        this.updateHighcharts()

        // } else {
        //     this.updateSeriesData(events)
        // }
    }

    createContainer() {
        const containerWrapper = this.el.querySelector('.widget-xrange-wrapper')
        if (!containerWrapper) {
            this.containerWrapper = document.createElement('div')
            this.el.appendChild(this.containerWrapper)
            this.containerWrapper.className = 'widget-xrange-wrapper'
            this.containerWrapper.style.cssText += '; width: 100%; padding-top: 0;'

            return this.containerWrapper
        }

        return containerWrapper
    }

    render() {
        this.onLoad()
        const self = this
        this.chart = new Highcharts.Chart({
            chart: {
                renderTo: this.createContainer(),
                resetZoomButton: {
                    theme: { style: { display: 'none' } }
                },
                type: HIGHCHARTS_WIDGET_TYPE,
                zoomType: 'x',
                panning: {
                    enabled: false
                },
                events: {
                    selection: event => this.changeTimespan(event),
                    redraw: function () {
                        self.maybeRegisterMouseListeners()
                        self.maybeForceExtremes()

                        const combinedWidget =
                            this.container.closest('.widget-type__value-xrange') ||
                            this.container.closest('.widget-type__list-xrange') ||
                            this.container.closest('.widget-type__value-xrange2') ||
                            this.container.closest('.widget-type__list-xrange2')
                        const chartWidth = this.chartWidth
                        const chartHeight = this.chartHeight
                        const categories = this.yAxis[0].categories
                        const leftContainerHeight = self.$el
                            .closest('.widget-content')
                            .find('.chart-left-container')
                            .prop('scrollHeight')

                        if (combinedWidget) {
                            if (combinedWidget.clientHeight < categories.length * 40) {
                                combinedWidget.classList.add('force-scroll-on-combined-widget')
                                self.$el.closest('.chart-right-container').height(`${leftContainerHeight + 36}px`)
                                this.container.style.height = `${leftContainerHeight + 10}px`
                            } else {
                                combinedWidget.classList.remove('force-scroll-on-combined-widget')
                                self.$el.closest('.chart-right-container').height(`auto`)
                                this.container.style.height = `auto`
                            }
                            this.reflow()
                        } else {
                            if (chartHeight < categories.length * 22) {
                                this.setSize(chartWidth, chartHeight * 2, false)
                                this.container.parentElement.parentElement.style.overflow = 'hidden auto'
                            }
                        }
                    }
                },
                marginLeft: this.cfg.marginLeft || null
            },
            plotOptions: {
                series: {
                    turboThreshold: 100000,
                    enableMouseTracking: true,
                    states: {
                        hover: {
                            enabled: true,
                            lineWidthPlus: 0
                        }
                    },
                    point: {
                        events: {
                            click: this.goToURL
                        }
                    }
                }
            },
            tooltip: {
                enabled: !this.cfg.hideTooltip,
                formatter: function (tooltip) {
                    // TODO: This formatter will not work if Shared is True
                    if (this.point) {
                        const begin = this.x
                        const end = this.x2
                        const timeDiff = end - begin

                        const formattedDuration = Widget.formatDuration(timeDiff);

                        const footerText = this.point.customTooltipText ? `<br/><em>${this.point.customTooltipText}</em>` : ''

                        const valueForDisplay = Array.isArray(this.point.value) ? this.point.value.join(',')
                            : this.point.value ? this.point.value : ''

                        return `
                            ${Highcharts.dateFormat('%b %e, %H:%M:%S', new Date(this.x))} - ${Highcharts.dateFormat(
                            '%b %e, %H:%M:%S',
                            new Date(this.x2)
                        )} <br />
                            <span style="color:${this.point.bulletColor || this.color}">●</span>: <strong>${this.yCategory}</strong> ${valueForDisplay}<br/>
                            ${formattedDuration && `Duration: <strong>${formattedDuration}</strong>`}
                            ${footerText}
                        `
                    }

                    return tooltip.defaultFormatter.call(this, tooltip)
                },
                followPointer: true,
                valueDecimals: 2
            },
            legend: {
                enabled: false
            },
            time: {
                useUTC: false
            },
            title: {
                text: ''
            },
            xAxis: {
                type: 'datetime',
                crosshair: {
                    snap: false
                },
                events: {
                    afterSetExtremes: function (e) {
                        self.setPreviousZoom(e)
                    }
                }
            },
            yAxis: {
                title: {
                    text: ''
                },
                categories: [],
                /*
                The categories are informed in a list, for example: ["category1", "category2", "category3"] with the smaller
                indexes at the top. yAxis default behavior is to display categories with smaller indexes at the bottom,
                by reversing the order we have a better match between the categories as informed by the user and the order
                they are displayed on the chart, with smaller indexes at the top and bigger at the bottom.
                 */
                reversed: true
            },
            series: [
                {
                    id: 'xRangeEvents',
                    name: '',
                    showInLegend: false,
                    minPointLength: 3,
                    // borderColor: 'gray',
                    pointWidth: null,
                    data: [],
                    dataLabels: {
                        enabled: false
                    }
                }
            ]
        })

        if (!this.handleMouse) {
            Highcharts.addEvent(this.el.firstChild, 'mousemove', e => {
                const chart = this.chart
                if (!chart || !chart?.pointer?.normalize) return

                const normalized = chart.pointer.normalize(e)
                const property = chart.userOptions.chart.inverted ? 'chartY' : 'chartX'
                const x = normalized[property]
                const xValue = chart.xAxis[0]?.toValue(x, false)
                pointService.currentPoint$.next({
                    x: xValue,
                    chart: chart,
                    event: normalized
                })
            })

            this.handleMouse = true
        }

        this.maybeRegisterMouseListeners()

        return this
    }

    // ==============================================================
    // Dashboard-synced ctrl+wheel zoom / shift+wheel pan
    //
    // Mirrors live-core's commonZoom mixin (webapp/js/live-core/widgets/highcharts-prototype.ts),
    // used by the temporal widget, so ctrl+wheel/shift+wheel over this chart zooms in sync with
    // every other widget sharing the same span type ('time') on the dashboard, instead of only
    // zooming this chart's own axis. The actual ctrl/shift + wheel key handling happens globally
    // in live-core's shortcut-manager, which broadcasts computed extremes through ZoomManager's
    // ZoomManagerZoomInNotifier$/ZoomManagerZoomOutNotifier$ - this widget only needs to apply
    // them to its own axis and report back the hovered span type + crosshair position.
    // ==============================================================

    getSpanType() {
        return 'time'
    }

    isWidgetDetachedFromDashboardSpan() {
        return false
    }

    isRealtimeSpan() {
        const normalized = this.span && this.span.normalized

        return typeof normalized === 'string' && normalized.indexOf('now') >= 0 && normalized.indexOf('shifted') < 0
    }

    initializeZoom() {
        if (!this.zoomInSubscription) {
            this.zoomInSubscription = ZoomManagerZoomInNotifier$.subscribe(zoom => this.setZoomIn(zoom))
        }

        if (!this.zoomOutSubscription) {
            this.zoomOutSubscription = ZoomManagerZoomOutNotifier$.subscribe(zoom => this.setZoomOut(zoom))
        }
    }

    destroyZoom() {
        if (typeof this.zoomInSubscription?.unsubscribe === 'function') this.zoomInSubscription.unsubscribe()
        if (typeof this.zoomOutSubscription?.unsubscribe === 'function') this.zoomOutSubscription.unsubscribe()
        if (typeof this.registeredMouseEnterListener === 'function') this.registeredMouseEnterListener()
        if (typeof this.registeredMouseLeaveListener === 'function') this.registeredMouseLeaveListener()
        if (typeof this.registeredMouseMoveZoomListener === 'function') this.registeredMouseMoveZoomListener()
    }

    setZoomIn(zoom) {
        if (zoom.newZoomBehavior && zoom.spanType === this.getSpanType()) {
            this.chartInRealTime = !!zoom.chartInRealTime
            this.toCommitAxisExtremes = true
            this.setAxisExtremes(zoom)
        }
    }

    setZoomOut(zoom) {
        if (zoom.newZoomBehavior && zoom.spanType === this.getSpanType()) {
            this.chartInRealTime = zoom.resetZoom ? true : !!zoom.chartInRealTime
            this.toCommitAxisExtremes = true
            this.setAxisExtremes(zoom)
        }
    }

    setAxisExtremes(zoom) {
        this.hasAxisExtremesSet = true

        if (zoom.newZoomBehavior) this.toSetPreviousZoom = zoom

        if (this.chart?.xAxis) {
            this.chart.xAxis.forEach(xAxis => xAxis.setExtremes?.(zoom.min, zoom.max, true))
        }
    }

    setPreviousZoom(appliedZoom) {
        const matchesRequestedZoom =
            this.toSetPreviousZoom &&
            appliedZoom &&
            appliedZoom.min === this.toSetPreviousZoom.min &&
            appliedZoom.max === this.toSetPreviousZoom.max

        if (matchesRequestedZoom) {
            ZoomManager.setPreviousZoom(this.toSetPreviousZoom)
            this.toSetPreviousZoom = null
            this.toCommitAxisExtremes = false
        }
    }

    maybeForceExtremes() {
        const prevExtreme = this.chart?.xAxis?.[0]?.getExtremes()
        const isValidPrevExtreme = typeof prevExtreme?.min === 'number' && typeof prevExtreme?.max === 'number'
        const prevZoom = ZoomManager.getPreviousZoom(this.getSpanType())
        const isValidPrevZoom = typeof prevZoom?.min === 'number' && typeof prevZoom?.max === 'number'

        // Axis extremes were just requested but haven't been committed via afterSetExtremes yet
        if (this.toCommitAxisExtremes) return

        // A real-time-flagged zoom (eg. reset) should not be reapplied over a fresh span reset
        if (this.hasAxisExtremesSet && this.chartInRealTime) return

        if (
            isValidPrevExtreme &&
            isValidPrevZoom &&
            (prevExtreme.min !== prevZoom.min || prevExtreme.max !== prevZoom.max)
        ) {
            this.setAxisExtremes(prevZoom)
        }
    }

    maybeRegisterMouseListeners() {
        if (!this.registeredMouseEnterListener && this.chart?.container) {
            this.registeredMouseEnterListener = Highcharts.addEvent(
                this.chart.container,
                'mouseenter',
                this.handleZoomMouseEnter.bind(this)
            )
        }

        if (!this.registeredMouseLeaveListener && this.chart?.container) {
            this.registeredMouseLeaveListener = Highcharts.addEvent(
                this.chart.container,
                'mouseleave',
                this.handleZoomMouseLeave.bind(this)
            )
        }

        if (!this.registeredMouseMoveZoomListener && this.chart?.container) {
            this.registeredMouseMoveZoomListener = Highcharts.addEvent(
                this.chart.container,
                'mousemove',
                this.handleZoomMouseMove.bind(this)
            )
        }
    }

    handleZoomMouseEnter() {
        if (!this.span) return

        ZoomManager.setSpanTypeExtremes(this.getSpanType(), this.span)
        ZoomManager.setHoveredWidgetSpanType(this.getSpanType())
        ZoomManager.setRealtimeSpan(this.getSpanType(), this.isRealtimeSpan())
    }

    handleZoomMouseLeave() {
        ZoomManager.setHoveredWidgetSpanType(null)
        ZoomManager.setCrosshairPosition(null)
    }

    handleZoomMouseMove(e) {
        if (!this.chart?.pointer) return

        const mouseX = this.chart.pointer.normalize(e).chartX
        const cursorX = this.chart.xAxis[0]?.toValue(mouseX)

        ZoomManager.setCrosshairPosition(cursorX)
    }

    destroy() {
        this.destroyZoom()
        super.destroy()
    }

    onEndHistory(type) {
        this.isFollowing = true

        // if (!this.snapshotEvents) {
        //     this.updateOpenIntervals()
        // }
    }

    onSpan(type, span) {
        this.span = span
        // this.chart.xAxis[0].setExtremes(span.begin, span.end)
        this.chart.update({ xAxis: { min: span.begin, max: span.end } })
        this.logger.debug('span', span, this.chart)
        // if (!this.snapshotEvents) {
        //     this.updateOpenIntervals()
        // }
    }

    onStart(type, startEvent) {
        this.isFollowing = false
        this.data = []

        // Discard per-event color tracking from any previous run before the new
        // query's metadata is applied.
        this.resetEventColorLegendEntries()

        // Configure is query sends snapshot
        // this.snapshotEvents = startEvent.info && startEvent.info.snapshot

        this.logger.debug('onStart', startEvent)
        // Use label from event metadata
        // i.e. => @meta ("Engine 1", "Engine 2"):seq as categories
        if (startEvent.info && startEvent.info.categories) {
            if (!Array.isArray(startEvent.info.categories)) {
                console.warn(
                    '@meta categories is expected to be an array, ensure you are using :seq from a Pipes query'
                )
                this.categories = []
            } else {
                this.categories = [...startEvent.info.categories]
            }
        } else {
            this.categories = []
        }
        this.updateCategories(this.categories)

        this.max_extrapolate_time_ms = startEvent.info && startEvent.info.max_extrapolate_time_ms

        if (startEvent.info && startEvent.info.colors && typeof startEvent.info.colors === 'object') {
            this.colorRules = startEvent.info.colors
        } else {
            this.colorRules = {}
        }
        if (startEvent.info && startEvent.info.durationLimits && typeof startEvent.info.durationLimits === 'object') {
            const limitsObj = startEvent.info.durationLimits

            /*
            Limits raw object looks like: {"270000.0": "#EE6B1D", "450000.0": "#EE1818" }
            Turn to array of objects with numeric limit and color for easier use:
            [{ limit: 270000, color: "#EE6B1D" }, { limit: 450000, color: "#EE1818" }]
            Sorted in descending order to apply the most restrictive limit first
             */
            this.durationLimitRules = Object.keys(limitsObj).map(key => ({
                limit: parseFloat(key),
                color: limitsObj[key]
            })).sort((a, b) => b.limit - a.limit)
        } else {
            this.durationLimitRules = []
        }

        // Apply the color legend proxy series immediately after colorRules are loaded
        // from the new query's start event, so the legend reflects the new query colors
        // before events arrive.
        this.syncLegendProxySeries()

        if (startEvent.info && startEvent.info.labelUrls) {
            const urls = startEvent.info.labelUrls

            this.chart.series[0].update({ cursor: 'pointer' }) // assume series are clickable too

            this.chart.yAxis[0].update({
                labels: {
                    style: { color: DEFAULT_COLOR, cursor: 'pointer', 'font-size': '13px' },

                    userHTML: true,
                    formatter: function (a) {
                        const url = this.pos < urls.length ? urls[this.pos] : null

                        if (url) {
                            return '<a href="' + url + '" target="_blank">' + this.value + '</a>'
                        } else {
                            return this.value
                        }
                    }
                }
            })
        } else {
            this.chart.series[0].update({ cursor: 'default' })
        }

        if (startEvent.info && startEvent.info.unbatchedMode) {
            this.unbatchedMode = true
        }

        window._xrange = this
    }

    goToURL = options => {
        const url = options.point.url

        if (url) window.open(url, '_blank')
    }

    changeTimespan = event => {
        const widgetIdx = this.options.widgetIndex ?? this.options?.widgetContext?.widgetIndex

        // This is not future proof, need to find a better way to see when the widget is linked
        const linked = $(this.getEl()).closest('.widget-container').find('img[src*="/linked-time"]')

        const begin = event.xAxis[0].min
        const end = event.xAxis[0].max

        const localFrom = moment(parseInt(begin, 10)).format(LOCAL_DATE_TIME_FORMAT)
        const localTo = moment(parseInt(end, 10)).format(LOCAL_DATE_TIME_FORMAT)

        if (linked.length && this.setTimespan) {
            if (event.xAxis) {
                this.setTimespan(`${localFrom} to ${localTo}`, undefined, true)
            } else {
                this.setTimespan(this.defaultTimespan, undefined, true)
            }
        } else {
            this.setTimespan(`${localFrom} to ${localTo}`, widgetIdx, true)
        }
    }

    updateCategories(categories) {
        this.categories = categories
        // this.get('xRangeEvents').yAxis.setCategories(this.categories)
        this.chart.update({
            yAxis: {
                categories: this.categories,
                min: 0,
                max: this.categories.length - 1
            }
        })
    }

    static formatDuration(timeDiff) {
        const minuteInMiliseconds = 60000
        const hourInMiliseconds = 3600000
        const dayInMiliseconds = 24 * 3600000
        const durationObj = moment.duration(timeDiff)

        if (timeDiff < minuteInMiliseconds) {
            return `${durationObj.seconds()}s`
        } else if (timeDiff < hourInMiliseconds) {
            return `${durationObj.minutes()}m ${durationObj.seconds()}s`
        } else if (timeDiff < dayInMiliseconds) {
            return `${durationObj.hours()}h ${durationObj.minutes()}m`
        } else {
            return `${Math.floor(
                timeDiff / dayInMiliseconds
            )}d ${durationObj.hours()}h ${durationObj.minutes()}m`
        }
    }

    // ==============================================================
    // Color legend proxy
    //
    // Problem:
    //   Highcharts xrange chart legends are designed to identify y-axis rows,
    //   not the colors of individual bars. When multi-color `colorRules` metadata
    //   is in use — or when individual events carry distinct `color` attributes —
    //   there is no built-in mechanism to expose a color key to the viewer.
    //
    // Workaround:
    //   Inject empty "proxy" series (type: scatter, data: []) whose sole purpose
    //   is to add a labeled, colored icon to the Highcharts legend.
    //   - The real xrange series is excluded from the legend via showInLegend: false.
    //   - The legend is only enabled when more than one distinct color is present;
    //     single-color charts need no legend.
    //   - Legend proxy series have no data, so they never render bars or markers on
    //     the chart. Their marker appears only in the legend symbol.
    //   - Legend item clicks are suppressed because these are decorative entries, not
    //     real data series that can be toggled.
    //
    // Sources for legend entries (in priority order):
    //   1. @meta colors object from the start event (this.colorRules) — preferred
    //      because labels and colors are explicitly declared in the query.
    //   2. Per-event `color` attribute, when the associated `value` is a stable
    //      scalar across all events. Entries where the same label maps to different
    //      colors across events are discarded as ambiguous.
    //
    // Lifecycle integration:
    //   - syncLegendProxySeries() is called in onStart (after colorRules are loaded
    //     from query metadata) so the legend is ready before events arrive.
    //   - syncLegendProxySeries() is also called inside updateHighcharts (after the
    //     main chart data is refreshed) so that event-derived colors are reflected as
    //     data flows in.
    //   - resetEventColorLegendEntries() is called in onStart and at the beginning of
    //     each onEvents batch to discard stale per-event color tracking from previous
    //     query runs.
    // ==============================================================

    static formatLegendLabel(value) {
        if (value === 1 || value === 1.0 || value === true || value === 'true') {
            return 'Active'
        }

        return value == null ? '' : `${value}`
    }

    static normalizeLegendColor(color) {
        return typeof color === 'string' && color.trim() !== '' ? color.trim().toLowerCase() : null
    }

    static isLegendCompatibleValue(value) {
        if (Array.isArray(value) || value == null) return false

        if (typeof value === 'string') {
            return value.trim() !== ''
        }

        return typeof value === 'number' || typeof value === 'boolean'
    }

    resetEventColorLegendEntries() {
        this.eventColorLegendEntries = {}
        this.ambiguousEventColorLegendEntries = new Set()
    }

    registerEventColorLegendEntry(event) {
        if (!Widget.isLegendCompatibleValue(event.value)) return

        const eventColor = Widget.normalizeLegendColor(event.color)
        if (!eventColor) return

        const rawValueKey = `${event.value}`
        if (this.colorRules[rawValueKey] || this.colorRules[event.value]) {
            // colorRules already covers this value — no need for an event-derived entry
            return
        }

        const label = Widget.formatLegendLabel(event.value)
        if (!label || this.ambiguousEventColorLegendEntries.has(label)) return

        const existing = this.eventColorLegendEntries[label]

        if (existing && Widget.normalizeLegendColor(existing.color) !== eventColor) {
            // Same label seen with two different colors → ambiguous, discard
            delete this.eventColorLegendEntries[label]
            this.ambiguousEventColorLegendEntries.add(label)
            return
        }

        this.eventColorLegendEntries[label] = {
            name: label,
            color: event.color.trim()
        }
    }

    getLegendEntries() {
        const entries = []
        const seenEntries = new Set()

        // Priority 1: colorRules from @meta colors metadata
        Object.entries(this.colorRules || {}).forEach(([value, color]) => {
            const normalizedColor = Widget.normalizeLegendColor(color)
            const name = Widget.formatLegendLabel(value)

            if (!normalizedColor || !name) return

            const entryKey = `${name}__${normalizedColor}`
            if (seenEntries.has(entryKey)) return

            seenEntries.add(entryKey)
            entries.push({ name, color: color.trim() })
        })

        // Priority 2: stable per-event color + value pairs not covered by colorRules
        Object.values(this.eventColorLegendEntries || {}).forEach(entry => {
            const normalizedColor = Widget.normalizeLegendColor(entry.color)
            if (!normalizedColor || !entry.name) return

            const entryKey = `${entry.name}__${normalizedColor}`
            if (seenEntries.has(entryKey)) return

            seenEntries.add(entryKey)
            entries.push(entry)
        })

        // Only return entries when there are at least two distinct colors;
        // a single-color chart does not need a legend.
        const distinctColors = new Set(entries.map(entry => Widget.normalizeLegendColor(entry.color)).filter(Boolean))

        return distinctColors.size > 1 ? entries : []
    }

    syncLegendProxySeries() {
        if (!this.chart) return

        const legendEntries = this.getLegendEntries()
        const chart = this.chart

        // Remove any previously injected proxy series before re-adding the current set
        chart.series
            .filter(series => series.options?.custom?.isColorLegendProxy)
            .forEach(series => {
                series.remove(false)
            })

        legendEntries.forEach((entry, index) => {
            chart.addSeries(
                {
                    id: `${LEGEND_PROXY_SERIES_PREFIX}${index}`,
                    name: entry.name,
                    color: entry.color,
                    // scatter with empty data draws nothing on the chart itself
                    type: 'scatter',
                    data: [],
                    enableMouseTracking: false,
                    showInLegend: true,
                    // A visible marker is required for the legend icon to render;
                    // because data is empty, no marker is drawn on the chart.
                    marker: {
                        radius: 6,
                        symbol: 'circle'
                    },
                    states: {
                        inactive: { enabled: false },
                        hover: { enabled: false }
                    },
                    // Prevent the default Highcharts legend click from toggling series
                    // visibility — these are decorative items, not real data series.
                    events: {
                        legendItemClick: function () {
                            return false
                        }
                    },
                    custom: {
                        isColorLegendProxy: true
                    }
                },
                false
            )
        })

        chart.update(
            {
                legend: {
                    enabled: legendEntries.length > 0
                }
            },
            false
        )

        chart.redraw()
    }
}

const Config = props => {
    const [labelWidth, setLabelWidth] = useState(props.config.marginLeft)

    const handleWidthChange = useCallback(
        debounce(width => {
            props.onChange(`marginLeft`, width)
        }, 500),
        []
    )

    useEffect(() => {
        handleWidthChange(labelWidth)
    }, [labelWidth])

    return (
        <div className="form-group">
            <label>
                Label Width (px)
                <HelpTip id="ticks-help" placement="top" text={'Leave empty for default width'} />
            </label>
            <input type="number" min="0" max="500" value={labelWidth} onChange={e => setLabelWidth(e.target.value)} />
        </div>
    )
}

const WidgetViewUIConfig = props => {
    const { onChangeLayoutConfig, config } = props
    const { hideTooltip } = config

    return (
        <div style={{ padding: '15px 18px' }}>
            <div className=" tooltip-options">
                <label className="group-label">Tooltip:</label>
                <ul className="tooltip-toggler tooltip-toggler--narrow" style={{ justifyContent: 'flex-start' }}>
                    <li
                        className={!hideTooltip && 'selected'}
                        onClick={() => onChangeLayoutConfig('hideTooltip', false)}
                    >
                        <div className="flex flex-center-items">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                className="tooltip__icon"
                            >
                                <g fill="none" fillRule="evenodd">
                                    <path fill="#364159" stroke="#fff" d="M.5.5v14h14V.5H.5z" />
                                    <path fill="#fff" d="M2 7h3v1H2zM7 7h6v1H7z" />
                                </g>
                            </svg>
                            On
                        </div>
                    </li>
                    <li className={hideTooltip && 'selected'} onClick={() => onChangeLayoutConfig('hideTooltip', true)}>
                        <div className="flex flex-center-items">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                className="tooltip__icon"
                            >
                                <g fill="none" fillRule="evenodd">
                                    <path fill="#364159" stroke="#fff" d="M.5.5v14h14V.5H.5z" />
                                    <path
                                        fill="#fff"
                                        fillRule="nonzero"
                                        d="M13.742 14.36l.617-.618L1.187.569l-.618.618z"
                                    />
                                    <path fill="#fff" d="M14.36 1.187l-.618-.618L.569 13.742l.618.617z" />
                                </g>
                            </svg>
                            Off
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    )
}
WidgetViewUIConfig.propTypes = {
    onChangeLayoutConfig: propTypes.func,
    config: {
        hideTooltip: propTypes.bool
    }
}

widgetsService.dashboardActions(WIDGET_TYPE, 'view', props => (
    <WidgetViewUIConfig config={props.widgetConfig} onChangeLayoutConfig={props.changeConfig} />
))

widgetsService.name(WIDGET_TYPE, 'XRange2')
widgetsService.description(
    WIDGET_TYPE,
    i18n(
        'The X-range series v2 displays ranges on the X axis, typically time intervals with a start and an end date. X-range is the basic series of a Gantt chart.'
    )
)
widgetsService.config(WIDGET_TYPE, { marginLeft: null, snapshot: true, ...DEFAULT_CHART_CONFIG })
widgetsService.ui(WIDGET_TYPE, {
    configForm: Config
})
widgetsService.impl(WIDGET_TYPE, Widget)
widgetsService.policies(WIDGET_TYPE, {
    ...Widget.policies,
    requireReactStateOnComponent: true
})

require('./index.scss')
